/**
 * Поисковый запрос → варианты написания.
 *
 * Геокодер Open-Meteo матчит ПРЕФИКС имени буквально, дефис считает значащим
 * символом и никакой нечёткости не делает. Замер 2026-09-15:
 *
 *   «санкт петербург» → 0      «санкт-петербург» → 2
 *   «нью йорк»        → 0      «нью-йорк»        → 3
 *   «ростов на дону»  → 0      «ростов-на-дону»  → 1
 *   «санкт»           → 3 (первый — Санкт-Петербург)
 *
 * Поэтому нечёткость приходится делать на клиенте: перебором вариантов.
 * Ступени идут от точной к широкой, вызывающий останавливается на первой
 * непустой — значит на обычном запросе лишних обращений к API не будет.
 */

/**
 * Короче трёх символов префикс не берём: «ла» из «ла пас» совпадёт с сотней
 * городов и уведёт выдачу неизвестно куда.
 */
const MIN_PREFIX = 3;

export function queryVariants(raw: string): string[] {
  const base = raw.trim().replace(/\s+/g, " ");
  if (!base) return [];

  const out = [base];
  const push = (v: string) => { if (v && !out.includes(v)) out.push(v); };

  if (base.includes(" ")) push(base.replace(/ /g, "-"));
  if (base.includes("-")) push(base.replace(/-/g, " "));

  // Последняя ступень — только первое слово. Она же вытягивает опечатку в
  // хвосте: «санкт-петербуг» целиком не найдётся, «санкт» найдётся.
  const first = base.split(/[\s-]/)[0] ?? "";
  if (first !== base && first.length >= MIN_PREFIX) push(first);

  return out;
}

export interface PlaceLike { name: string; lat: number; lon: number }

/**
 * Близнецы приходят от обоих источников, но по-разному, поэтому и правил два.
 *
 * Open-Meteo по «санкт-петербург» отдаёт «Санкт-Петербург, Россия» и
 * «Санкт-Петербург» без страны — имена разные, координаты разошлись в
 * третьем знаке. Ловится сравнением координат.
 *
 * OSM по «лисабон» отдаёт центроид города и центроид округа — подпись
 * одна и та же, а координаты разошлись сильнее порога. Ловится сравнением
 * подписи: два ряда с одинаковым текстом человек всё равно не различит,
 * так что второй бесполезен независимо от того, то же это место или нет.
 *
 * Порядок сохраняем: оба источника сортируют по релевантности, а osmPlaces
 * вдобавок поднимает населённые пункты над областями.
 */
const SAME_PLACE_DEG = 0.1;

export function dedupePlaces<T extends PlaceLike>(places: T[]): T[] {
  const out: T[] = [];
  for (const p of places) {
    const twin = out.some(
      (k) =>
        k.name === p.name ||
        (Math.abs(k.lat - p.lat) < SAME_PLACE_DEG && Math.abs(k.lon - p.lon) < SAME_PLACE_DEG),
    );
    if (!twin) out.push(p);
  }
  return out;
}

export interface Hit<T> {
  /** Какая ступень сработала — её показываем, если это не то, что набрали. */
  query: string;
  hits: T[];
  /** Сработала первая ступень, то есть запрос набран так, как город и называется. */
  exact: boolean;
}

/**
 * Обход лестницы: спрашиваем ступень за ступенью, останавливаемся на первой
 * непустой. `exact` решает, как повести себя поверхности: точное попадание
 * можно запускать сразу, а спуск на запасную ступень надо показать — иначе
 * «санкт» молча уедет в Санкт-Галлен, и человек не поймёт, почему.
 */
export async function firstHit<T extends PlaceLike>(
  raw: string,
  lookup: (q: string) => Promise<T[]>,
): Promise<Hit<T>> {
  const variants = queryVariants(raw);
  for (const [i, v] of variants.entries()) {
    const found = await lookup(v);
    if (found.length) return { query: v, hits: dedupePlaces(found), exact: i === 0 };
  }
  return { query: variants[0] ?? "", hits: [], exact: false };
}
